import { useEffect, useState } from 'react';

const detectStandaloneMode = () => {
  if (typeof window === 'undefined') return false;

  const matchMediaStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;

  const iosStandalone = typeof window.navigator !== 'undefined' && window.navigator.standalone;
  const androidStandalone =
    typeof document !== 'undefined' && document.referrer?.startsWith('android-app://');

  return Boolean(matchMediaStandalone || iosStandalone || androidStandalone);
};

const detectMobileDevice = () => {
  if (typeof window === 'undefined') return false;

  const nav = typeof navigator !== 'undefined' ? navigator : window.navigator;
  const userAgent = nav?.userAgent || nav?.vendor || window.opera || '';
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  const viewportWidth = window.innerWidth || window.screen?.width || 0;

  return mobileRegex.test(userAgent) || viewportWidth <= 900;
};

const usePwaDisplayMode = () => {
  const [isStandalone, setIsStandalone] = useState(() => detectStandaloneMode());
  const [isMobile, setIsMobile] = useState(() => detectMobileDevice());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateStandalone = () => {
      setIsStandalone(detectStandaloneMode());
    };

    const updateMobile = () => {
      setIsMobile(detectMobileDevice());
    };

    let mediaQuery = null;
    if (typeof window.matchMedia === 'function') {
      mediaQuery = window.matchMedia('(display-mode: standalone)');
      const mediaHandler = (event) => setIsStandalone(event.matches || detectStandaloneMode());
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', mediaHandler);
      } else if (mediaQuery.addListener) {
        mediaQuery.addListener(mediaHandler);
      }

      // Cleanup media query listener
      const cleanupMedia = () => {
        if (!mediaQuery) return;
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', mediaHandler);
        } else if (mediaQuery.removeListener) {
          mediaQuery.removeListener(mediaHandler);
        }
      };

      window.addEventListener('beforeinstallprompt', updateStandalone);
      window.addEventListener('appinstalled', updateStandalone);
      window.addEventListener('DOMContentLoaded', updateStandalone);
      window.addEventListener('resize', updateMobile);
      window.addEventListener('orientationchange', updateMobile);

      // Initial sync
      updateStandalone();
      updateMobile();

      return () => {
        cleanupMedia();
        window.removeEventListener('beforeinstallprompt', updateStandalone);
        window.removeEventListener('appinstalled', updateStandalone);
        window.removeEventListener('DOMContentLoaded', updateStandalone);
        window.removeEventListener('resize', updateMobile);
        window.removeEventListener('orientationchange', updateMobile);
      };
    }

    // Fallback when matchMedia is not available
    updateStandalone();
    updateMobile();

    window.addEventListener('beforeinstallprompt', updateStandalone);
    window.addEventListener('appinstalled', updateStandalone);
    window.addEventListener('DOMContentLoaded', updateStandalone);
    window.addEventListener('resize', updateMobile);
    window.addEventListener('orientationchange', updateMobile);

    return () => {
      window.removeEventListener('beforeinstallprompt', updateStandalone);
      window.removeEventListener('appinstalled', updateStandalone);
      window.removeEventListener('DOMContentLoaded', updateStandalone);
      window.removeEventListener('resize', updateMobile);
      window.removeEventListener('orientationchange', updateMobile);
    };
  }, []);

  return {
    isStandalone,
    isMobile,
    isPwaExperience: isStandalone && isMobile,
  };
};

export default usePwaDisplayMode;
